/**
 * useEcosystemData Hooks
 * 
 * Data hooks for the Ecosystem Integration layer.
 * Queries + mutations for ecosystem entities, event series, event instances,
 * artifact/offering links, entity relationships, and entity detail panels.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
// BAT-A2: bound the `allProviderIds` IN(...) clauses in
// `useEntityDetail`. Provider hierarchies (one parent + N children) are
// typically small (10–50), but legacy or org-wide entries can nest 100+
// children, and a regression in the relationships table (cycles, mass
// imports) could push the count higher. Routing the 3 sister `.in(
// 'provider_id', allProviderIds)` reads through chunkInQuery makes the
// cost shape predictable rather than relying on hierarchy-shape
// assumptions that drift over time.
import { chunkInQuery } from '@/utils/chunkInQuery';
import type {
  TrainingEventSeries,
  TrainingEventInstance,
  OrgKind,
  PriorityTier,
  EcosystemEntityDetail,
  ProviderAffiliation,
  ProviderMetadataContract,
  EntityRelationship,
  EntityRelationshipType,
} from '@/types/ecosystem';

// ═══ QUERY HOOKS ═════════════════════════════════════════════

interface EcosystemOrgFilters {
  tier?: PriorityTier;
  org_kind?: OrgKind;
  search?: string;
}

export function useEcosystemOrganizations(opts?: EcosystemOrgFilters) {
  const { tier, org_kind, search } = opts || {};

  return useQuery({
    queryKey: ['ecosystem-organizations', { tier, org_kind, search }],
    queryFn: async () => {
      let query = supabase
        .from('training_providers')
        .select('*')
        .or('stable_key.not.is.null,org_kind.not.is.null')
        .order('priority_tier', { ascending: true, nullsFirst: false })
        .order('display_name');

      if (tier) query = query.eq('priority_tier', tier);
      if (org_kind) query = query.eq('org_kind', org_kind);
      if (search) query = query.ilike('display_name', `%${search}%`);

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });
}

// ═══ RELATIONSHIP HOOKS ══════════════════════════════════════

/**
 * Fetch ALL entity_relationships with joined parent/child names.
 * Used by the tree builder.
 */
export function useAllEntityRelationships() {
  return useQuery({
    queryKey: ['entity-relationships-all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('entity_relationships')
        .select(`
          id,
          parent_id,
          child_id,
          relationship_type,
          is_primary,
          needs_review,
          created_at,
          parent:training_providers!entity_relationships_parent_id_fkey(id, display_name),
          child:training_providers!entity_relationships_child_id_fkey(id, display_name)
        `)
        .order('created_at');

      if (error) throw error;

      return (data || []).map((r: any) => ({
        id: r.id,
        parent_id: r.parent_id,
        child_id: r.child_id,
        relationship_type: r.relationship_type as EntityRelationshipType,
        is_primary: r.is_primary,
        needs_review: r.needs_review,
        created_at: r.created_at,
        parent_name: r.parent?.display_name || '',
        child_name: r.child?.display_name || '',
      })) as EntityRelationship[];
    },
  });
}

/**
 * Fetch relationships for a specific entity (as parent or child).
 * Used by the detail panel.
 */
export function useEntityRelationships(entityId: string | null) {
  return useQuery({
    queryKey: ['entity-relationships', entityId],
    queryFn: async () => {
      if (!entityId) return { asParent: [], asChild: [] };

      const [parentRes, childRes] = await Promise.all([
        supabase
          .from('entity_relationships')
          .select(`
            id, parent_id, child_id, relationship_type, is_primary, needs_review, created_at,
            child:training_providers!entity_relationships_child_id_fkey(id, display_name)
          `)
          .eq('parent_id', entityId)
          .order('created_at'),
        supabase
          .from('entity_relationships')
          .select(`
            id, parent_id, child_id, relationship_type, is_primary, needs_review, created_at,
            parent:training_providers!entity_relationships_parent_id_fkey(id, display_name)
          `)
          .eq('child_id', entityId)
          .order('created_at'),
      ]);

      if (parentRes.error) throw parentRes.error;
      if (childRes.error) throw childRes.error;

      const asParent = (parentRes.data || []).map((r: any) => ({
        ...r,
        child_name: r.child?.display_name || '',
      })) as EntityRelationship[];

      const asChild = (childRes.data || []).map((r: any) => ({
        ...r,
        parent_name: r.parent?.display_name || '',
      })) as EntityRelationship[];

      return { asParent, asChild };
    },
    enabled: !!entityId,
  });
}

/**
 * Build all parent breadcrumb paths for an entity (multi-path).
 * Walks up the relationship graph, capped at depth 6.
 */
export function useEntityParentPaths(entityId: string | null) {
  const { data: allRels } = useAllEntityRelationships();
  const { data: allOrgs } = useEcosystemOrganizations();

  return useQuery({
    queryKey: ['entity-parent-paths', entityId, allRels?.length, allOrgs?.length],
    queryFn: () => {
      if (!entityId || !allRels || !allOrgs) return [];

      const nameMap = new Map<string, string>();
      for (const o of allOrgs) nameMap.set(o.id, (o as any).display_name);

      // Build child→parents lookup (only approved)
      const childToParents = new Map<string, Array<{ parentId: string; relType: EntityRelationshipType }>>();
      for (const r of allRels) {
        if (r.needs_review) continue;
        if (!childToParents.has(r.child_id)) childToParents.set(r.child_id, []);
        childToParents.get(r.child_id)!.push({ parentId: r.parent_id, relType: r.relationship_type });
      }

      type PathSegment = { id: string; name: string; relType: EntityRelationshipType };
      const paths: PathSegment[][] = [];

      function walk(currentId: string, path: PathSegment[], depth: number) {
        if (depth > 6) return;
        const parents = childToParents.get(currentId);
        if (!parents || parents.length === 0) {
          if (path.length > 0) paths.push([...path].reverse());
          return;
        }
        for (const p of parents) {
          path.push({ id: p.parentId, name: nameMap.get(p.parentId) || p.parentId.substring(0, 8), relType: p.relType });
          walk(p.parentId, path, depth + 1);
          path.pop();
        }
      }

      walk(entityId, [], 0);
      return paths;
    },
    enabled: !!entityId && !!allRels && !!allOrgs,
  });
}

// ═══ EVENT HOOKS ═════════════════════════════════════════════

interface EventSeriesFilters {
  organizer_provider_id?: string;
}

export function useEcosystemEventSeries(opts?: EventSeriesFilters) {
  const { organizer_provider_id } = opts || {};

  return useQuery({
    queryKey: ['ecosystem-event-series', { organizer_provider_id }],
    queryFn: async () => {
      let query = supabase
        .from('training_event_series')
        .select(`
          *,
          organizer:training_providers!training_event_series_organizer_provider_id_fkey(
            id, display_name, stable_key
          )
        `)
        .eq('is_active', true)
        .order('series_name');

      if (organizer_provider_id) {
        query = query.eq('organizer_provider_id', organizer_provider_id);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as TrainingEventSeries[];
    },
  });
}

interface EventInstanceFilters {
  fromDate?: string;
  toDate?: string;
  onlyActive?: boolean;
  seriesId?: string;
  tracks?: string[];
}

export function useEcosystemEvents(opts?: EventInstanceFilters) {
  const { fromDate, toDate, onlyActive = true, seriesId, tracks } = opts || {};

  return useQuery({
    queryKey: ['ecosystem-event-instances', { fromDate, toDate, onlyActive, seriesId, tracks }],
    queryFn: async () => {
      let query = supabase
        .from('training_event_instances')
        .select(`
          *,
          series:training_event_series(
            id, stable_key, series_name, frequency,
            organizer:training_providers!training_event_series_organizer_provider_id_fkey(
              id, display_name, stable_key
            )
          )
        `)
        .order('start_date', { ascending: true });

      if (onlyActive) query = query.eq('is_active', true);
      if (fromDate) query = query.gte('start_date', fromDate);
      if (toDate) query = query.lte('start_date', toDate);
      if (seriesId) query = query.eq('series_id', seriesId);
      if (tracks && tracks.length > 0) query = query.overlaps('tracks', tracks);

      const { data, error } = await query;
      if (error) throw error;
      return data as TrainingEventInstance[];
    },
  });
}

export function useEventArtifacts(eventInstanceId: string | null) {
  return useQuery({
    queryKey: ['ecosystem-event-artifacts', eventInstanceId],
    queryFn: async () => {
      if (!eventInstanceId) return [];
      const { data, error } = await supabase
        .from('training_event_artifacts')
        .select(`
          *,
          artifact:training_artifacts(id, title, artifact_type, provider_name),
          offering:training_offering_instances(id, start_datetime, end_datetime, delivery_type, city, state)
        `)
        .eq('event_instance_id', eventInstanceId)
        .order('session_code');

      if (error) throw error;
      return data;
    },
    enabled: !!eventInstanceId,
  });
}

// ═══ ENTITY DETAIL QUERY ════════════════════════════════════

export function useEcosystemEntityDetail(entityId: string | null) {
  return useQuery({
    queryKey: ['ecosystem-entity-detail', entityId],
    queryFn: async (): Promise<EcosystemEntityDetail> => {
      if (!entityId) throw new Error('No entityId');

      // Fetch children via entity_relationships
      const { data: childRels } = await supabase
        .from('entity_relationships')
        .select('child_id')
        .eq('parent_id', entityId);

      const childIds = (childRels || []).map((r: any) => r.child_id);

      // Fetch child provider details
      // BAT-A2: chunk childIds. Note: pre-A2 the result was sorted
      // by display_name in a single query; with chunking each chunk
      // arrives sorted, then we apply a final concat-then-sort to
      // preserve the post-condition. (chunkInQuery's first-error-wins
      // contract is fine here — childProviders is read-only display.)
      let childProviders: any[] = [];
      if (childIds.length > 0) {
        const childRes = await chunkInQuery<
          string,
          { id: string; display_name: string; org_kind: string; stable_key: string }
        >(childIds, (chunk) =>
          supabase
            .from('training_providers')
            .select('id, display_name, org_kind, stable_key')
            .in('id', chunk)
            .order('display_name'),
        );
        // Re-sort across chunks so the merged result is monotonic by
        // display_name regardless of how the chunk boundary fell.
        childProviders = childRes.data
          .slice()
          .sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''));
      }

      const allProviderIds = [entityId, ...childIds];

      // Fetch sources for self + children
      // BAT-A2: chunk allProviderIds. As above, re-sort across chunks
      // to preserve the pre-A2 created_at-DESC ordering.
      const sourcesRes = await chunkInQuery<
        string,
        {
          id: string;
          source_type: string;
          source_identifier: string;
          source_name: string;
          created_at?: string;
        }
      >(allProviderIds, (chunk) =>
        supabase
          .from('training_sources')
          .select('id, source_type, source_identifier, source_name, created_at')
          .in('provider_id', chunk)
          .order('created_at', { ascending: false }),
      );
      const sources = sourcesRes.data
        .slice()
        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

      // Fetch offerings for self + children
      // BAT-A2: chunk + cross-chunk sort by start_datetime DESC.
      const offeringsRes = await chunkInQuery<
        string,
        {
          id: string;
          start_datetime: string;
          end_datetime: string;
          delivery_type: string;
          status: string;
        }
      >(allProviderIds, (chunk) =>
        supabase
          .from('training_offering_instances')
          .select('id, start_datetime, end_datetime, delivery_type, status')
          .in('provider_id', chunk)
          .order('start_datetime', { ascending: false }),
      );
      const offerings = offeringsRes.data
        .slice()
        .sort((a, b) => (b.start_datetime || '').localeCompare(a.start_datetime || ''));

      // Fetch artifacts for self + children
      // BAT-A2: chunk + cross-chunk sort by title ASC.
      const artifactsRes = await chunkInQuery<
        string,
        {
          id: string;
          title: string;
          artifact_type: string;
          provider_name: string;
        }
      >(allProviderIds, (chunk) =>
        supabase
          .from('training_artifacts')
          .select('id, title, artifact_type, provider_name')
          .in('provider_id', chunk)
          .order('title'),
      );
      const artifacts = artifactsRes.data
        .slice()
        .sort((a, b) => (a.title || '').localeCompare(b.title || ''));

      // Fetch event series organized by this entity
      const { data: eventSeries } = await supabase
        .from('training_event_series')
        .select(`
          *,
          organizer:training_providers!training_event_series_organizer_provider_id_fkey(
            id, display_name, stable_key
          )
        `)
        .eq('organizer_provider_id', entityId)
        .order('series_name');

      // Fetch affiliations from provider_metadata
      const { data: entityData } = await supabase
        .from('training_providers')
        .select('provider_metadata')
        .eq('id', entityId)
        .single();

      const metadata = (entityData?.provider_metadata || {}) as ProviderMetadataContract;
      const affiliations: ProviderAffiliation[] = metadata.affiliations || [];

      // Fetch relationships (as parent and as child)
      const [asParentRes, asChildRes] = await Promise.all([
        supabase
          .from('entity_relationships')
          .select(`
            id, parent_id, child_id, relationship_type, is_primary, needs_review, created_at,
            child:training_providers!entity_relationships_child_id_fkey(id, display_name)
          `)
          .eq('parent_id', entityId),
        supabase
          .from('entity_relationships')
          .select(`
            id, parent_id, child_id, relationship_type, is_primary, needs_review, created_at,
            parent:training_providers!entity_relationships_parent_id_fkey(id, display_name)
          `)
          .eq('child_id', entityId),
      ]);

      const asParent = (asParentRes.data || []).map((r: any) => ({
        ...r,
        child_name: r.child?.display_name || '',
      })) as EntityRelationship[];

      const asChild = (asChildRes.data || []).map((r: any) => ({
        ...r,
        parent_name: r.parent?.display_name || '',
      })) as EntityRelationship[];

      return {
        childProviders: childProviders || [],
        sources: sources || [],
        offerings: offerings || [],
        artifacts: artifacts || [],
        eventSeries: (eventSeries || []) as TrainingEventSeries[],
        affiliations,
        relationships: { asParent, asChild },
        counts: {
          providers: childProviders?.length || 0,
          sources: sources?.length || 0,
          offerings: offerings?.length || 0,
          eventSeries: eventSeries?.length || 0,
          artifacts: artifacts?.length || 0,
          affiliations: affiliations.length,
          relationships: asParent.length + asChild.length,
        },
      };
    },
    enabled: !!entityId,
  });
}

// ═══ MUTATION HOOKS ══════════════════════════════════════════

const ECOSYSTEM_QUERY_KEYS = [
  'ecosystem-organizations',
  'ecosystem-event-series',
  'ecosystem-event-instances',
  'ecosystem-event-artifacts',
  'ecosystem-entity-detail',
  'entity-relationships-all',
  'entity-relationships',
  'entity-parent-paths',
  'training-providers',
  'training-providers-extended',
];

function useEcosystemMutation<TInput>(actionName: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: TInput) => {
      const { data, error } = await supabase.functions.invoke('manage-provider', {
        body: { action: actionName, ...input },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      for (const key of ECOSYSTEM_QUERY_KEYS) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

interface CreateEntityInput {
  data: Record<string, unknown>;
  userId?: string;
  reason?: string;
}

export function useCreateEcosystemEntity() {
  return useEcosystemMutation<CreateEntityInput>('create');
}

interface UpdateEntityInput {
  providerId: string;
  data: Record<string, unknown>;
  userId?: string;
  reason?: string;
}

export function useUpdateEcosystemEntity() {
  return useEcosystemMutation<UpdateEntityInput>('update');
}

interface CreateSeriesInput {
  data: Record<string, unknown>;
  userId?: string;
  reason?: string;
}

export function useCreateEventSeries() {
  return useEcosystemMutation<CreateSeriesInput>('create_series');
}

interface UpdateSeriesInput {
  data: Record<string, unknown>;
  userId?: string;
}

export function useUpdateEventSeries() {
  return useEcosystemMutation<UpdateSeriesInput>('update_series');
}

interface CreateInstanceInput {
  data: Record<string, unknown>;
  userId?: string;
  reason?: string;
}

export function useCreateEventInstance() {
  return useEcosystemMutation<CreateInstanceInput>('create_instance');
}

interface UpdateInstanceInput {
  data: Record<string, unknown>;
  userId?: string;
}

export function useUpdateEventInstance() {
  return useEcosystemMutation<UpdateInstanceInput>('update_instance');
}

interface LinkArtifactInput {
  data: {
    event_instance_id: string;
    artifact_id: string;
    offering_instance_id?: string;
    session_code?: string;
    room_or_track?: string;
  };
}

export function useLinkArtifactToEvent() {
  return useEcosystemMutation<LinkArtifactInput>('link_artifact_to_event');
}

interface UnlinkInput {
  data: { linkId: string };
}

export function useUnlinkArtifactFromEvent() {
  return useEcosystemMutation<UnlinkInput>('unlink_artifact_from_event');
}

interface LinkOfferingInput {
  data: {
    event_instance_id: string;
    offering_instance_id: string;
    session_code?: string;
  };
}

export function useLinkOfferingToEvent() {
  return useEcosystemMutation<LinkOfferingInput>('link_offering_to_event');
}

export function useUnlinkOfferingFromEvent() {
  return useEcosystemMutation<UnlinkInput>('unlink_offering_from_event');
}

// ═══ RELATIONSHIP MUTATIONS ═════════════════════════════════

interface AddRelationshipInput {
  data: {
    parent_id: string;
    child_id: string;
    relationship_type: EntityRelationshipType;
    is_primary?: boolean;
    needs_review?: boolean;
  };
  userId?: string;
  reason?: string;
}

export function useAddRelationship() {
  return useEcosystemMutation<AddRelationshipInput>('add_relationship');
}

interface RemoveRelationshipInput {
  data: { relationship_id: string };
  userId?: string;
  reason?: string;
}

export function useRemoveRelationship() {
  return useEcosystemMutation<RemoveRelationshipInput>('remove_relationship');
}

interface ApproveRelationshipInput {
  data: { relationship_id: string };
  userId?: string;
  reason?: string;
}

export function useApproveRelationship() {
  return useEcosystemMutation<ApproveRelationshipInput>('approve_relationship');
}
